<template>
  <div class="modal-card card">
    <div class="card-header">
      <h4 class="card-header-title">Export Details</h4>
      <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
    </div>
    <div class="card-body">
      <div v-if="loading" class="text-center py-4">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="mt-2 text-muted">Loading export details...</p>
      </div>

      <div v-else-if="error" class="alert alert-danger">
        {{ error }}
      </div>

      <div v-else-if="executionResult">
        <!-- Execution Summary -->
        <div class="row mb-4">
          <div class="col-md-6">
            <div class="mb-3">
              <label class="form-label fw-bold text-muted small">Execution ID</label>
              <p class="mb-0 fw-bold font-monospace">{{ execution?.audsum_execution_id || executionResult?.id ||
                executionResult?.execution_id || '-' }}</p>
            </div>
          </div>
          <div class="col-md-6">
            <div class="mb-3">
              <label class="form-label fw-bold text-muted small">Status</label>
              <p class="mb-0">
                <span :class="getStatusBadgeClass(executionResult?.status)">
                  {{ executionResult?.status || '-' }}
                </span>
              </p>
            </div>
          </div>
          <div class="col-md-6">
            <div class="mb-3">
              <label class="form-label fw-bold text-muted small">Executed By</label>
              <p class="mb-0">{{ execution?.executed_by || '-' }}</p>
            </div>
          </div>
          <div class="col-md-6">
            <div class="mb-3">
              <label class="form-label fw-bold text-muted small">Executed Time</label>
              <p class="mb-0">{{ formatDateTime(execution?.executed_time) }}</p>
            </div>
          </div>
          <!-- <div class="col-md-6" v-if="execution?.count !== null && execution?.count !== undefined">
            <div class="mb-3">
              <label class="form-label fw-bold text-muted small">Count</label>
              <p class="mb-0">{{ formatNumber(execution.count) }}</p>
            </div>
          </div> -->
          <div class="col-md-6">
            <div class="mb-3">
              <label class="form-label fw-bold text-muted small">Output Location</label>
              <p class="mb-0 font-monospace small">{{ execution?.output_location || '-' }}</p>
            </div>
          </div>
        </div>

        <!-- Results Display -->
        <div v-if="executionResult?.data" class="border-top pt-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h4 class="mb-0">
              <i class="fa fa-check-circle text-success me-2"></i>
              Export Results
            </h4>
          </div>

          <div class="total-count-card mb-4">
            <div class="card bg-primary text-white">
              <div class="card-body text-center py-4">
                <h6 class="text-uppercase text-white-50 mb-2">
                  Total Rows
                </h6>
                <div class="d-flex align-items-center justify-content-center">
                  <h1 class="display-4 fw-bold mb-0">{{ formatNumber(executionResult?.data?.body?.length || 0) }}</h1>
                </div>
              </div>
            </div>
          </div>

          <!-- Preview -->
          <div v-if="executionResult.data.header && executionResult.data.body" class="mt-3">
            <label class="form-label text-muted small mb-2">Preview (first 10 rows)</label>
            <div class="table-responsive" style="max-height: 300px; overflow-y: auto;">
              <table class="table table-sm table-bordered table-hover">
                <thead class="table-light sticky-top">
                  <tr>
                    <th v-for="(header, index) in executionResult.data.header" :key="index" class="text-nowrap">
                      {{ header }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, rowIndex) in executionResult.data.body.slice(0, 10)" :key="rowIndex">
                    <td v-for="(cell, cellIndex) in row" :key="cellIndex">
                      {{ cell }}
                    </td>
                  </tr>
                </tbody>
              </table>
              <div v-if="executionResult.data.body.length > 10" class="text-muted small mt-2 text-center">
                ... and {{ formatNumber(executionResult.data.body.length - 10) }} more rows
              </div>
            </div>
          </div>

          <div class="mt-3">
            <InputField type="radio" v-model="exportFormat" :name="'exportFormat'" :options="formatOptions"
              :label="'Export Format'" :toolTip="'Select the format for downloading the exported data'" />
          </div>
        </div>

        <!-- No Data Message -->
        <div v-else class="text-center py-4 text-muted">
          <i class="fa fa-info-circle fa-2x mb-3"></i>
          <p>No export data available</p>
        </div>
      </div>
    </div>
    <div class="card-footer">
      <button type="button" class="btn btn-secondary" @click="refreshExecution" :disabled="loading">
        <span v-if="loading" class="spinner-border spinner-border-sm me-2" />
        <i v-else class="fa fa-refresh me-1"></i>
        Refresh
      </button>
      <button v-if="executionResult?.data && executionResult.data.body && executionResult.data.body.length > 0"
        type="button" class="btn btn-primary ms-2" @click="downloadResults">
        <i class="fa fa-download me-1"></i>
        Download {{ exportFormat.toUpperCase() }}
      </button>
      <button v-if="executionResult?.data && executionResult.data.body && executionResult.data.body.length > 0"
        type="button" class="btn btn-outline-primary ms-2" @click="copyToClipboard">
        <i class="fa fa-copy me-1"></i>
        Copy to Clipboard
      </button>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import InputField from '../InputField.vue'
import { DateTimeFormat } from '../../utils/commonFunction'

export default {
  name: 'ExportUserIdsDetail',
  components: { InputField },
  props: {
    modalData: Object,
  },
  data() {
    return {
      loading: false,
      error: null,
      executionResult: null,
      exportFormat: 'json',
    }
  },
  computed: {
    execution() {
      return this.modalData?.execution
    },
    audience() {
      return this.modalData?.audience
    },
    dataEnvironmentId() {
      return this.modalData?.dataEnvironmentId || this.audience?.audsum_data_environment
    },
    dataset() {
      return this.modalData?.dataset || this.audience?.audsum_dataset
    },
    formatOptions() {
      return [
        {
          index: 0,
          label: 'CSV',
          value: 'csv'
        },
        {
          index: 1,
          label: 'JSON',
          value: 'json'
        }
      ]
    },
  },
  methods: {
    ...mapActions({
      fetchExecutionResult: 'audience/fetchExecutionResult',
      addToast: 'addToast',
    }),
    async loadExecutionResult() {
      if (!this.dataEnvironmentId || !this.dataset || !this.execution?.audsum_execution_id) {
        this.error = 'Missing required information to load execution result'
        return
      }

      this.loading = true
      this.error = null

      try {
        this.executionResult = await this.fetchExecutionResult({
          dataEnvironmentId: this.dataEnvironmentId,
          dataset: this.dataset,
          executionId: this.execution.audsum_execution_id,
        })
      } catch (error) {
        this.error = error.response?.data?.message || error.message || 'Failed to load export details'
        console.error('Error loading execution result:', error)
      } finally {
        this.loading = false
      }
    },
    async refreshExecution() {
      await this.loadExecutionResult()
    },
    formatDateTime(dateString) {
      if (!dateString) return '-'
      return DateTimeFormat(dateString)
    },
    formatNumber(num) {
      if (num == null) return '-'
      return num.toLocaleString()
    },
    getStatusBadgeClass(status) {
      const statusLower = (status || '').toLowerCase()
      if (statusLower === 'success' || statusLower === 'succeeded' || statusLower === 'completed') {
        return 'badge bg-success'
      } else if (statusLower === 'failed' || statusLower === 'error') {
        return 'badge bg-danger'
      } else if (statusLower === 'running' || statusLower === 'in_progress') {
        return 'badge bg-warning'
      } else if (statusLower === 'pending' || statusLower === 'queued') {
        return 'badge bg-info'
      }
      return 'badge bg-secondary'
    },
    downloadResults() {
      if (!this.executionResult?.data) return

      let content, mimeType, extension

      if (this.exportFormat === 'csv') {
        const headers = this.executionResult.data.header.join(',')
        const rows = this.executionResult.data.body.map(row => row.join(',')).join('\n')
        content = headers + '\n' + rows
        mimeType = 'text/csv;charset=utf-8;'
        extension = 'csv'
      } else {
        content = JSON.stringify({
          columns: this.executionResult.data.header,
          data: this.executionResult.data.body,
        }, null, 2)
        mimeType = 'application/json;charset=utf-8;'
        extension = 'json'
      }

      const blob = new Blob([content], { type: mimeType })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      const executionId = this.execution?.audsum_execution_id || this.executionResult?.execution_id || 'export'
      link.download = `${executionId}.${extension}`
      link.click()
      URL.revokeObjectURL(link.href)

      this.addToast({ type: 'success', message: `${extension.toUpperCase()} downloaded successfully` })
    },
    async copyToClipboard() {
      if (!this.executionResult?.data) return

      try {
        const headers = this.executionResult.data.header.join('\t')
        const rows = this.executionResult.data.body.map(row => row.join('\t')).join('\n')
        const text = headers + '\n' + rows

        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text)
        } else {
          // Fallback for older browsers
          const input = document.createElement('textarea')
          input.style.position = 'absolute'
          input.style.left = '-9999px'
          input.value = text
          document.body.appendChild(input)
          input.select()
          document.execCommand('copy')
          document.body.removeChild(input)
        }
        this.addToast({ type: 'success', message: 'Data copied to clipboard' })
      } catch (error) {
        console.log(error)
        this.addToast({ type: 'error', message: 'Failed to copy to clipboard' })
      }
    },
  },
  mounted() {
    this.loadExecutionResult()
  },
}
</script>

<style scoped>
.form-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.sticky-top {
  position: sticky;
  top: 0;
  z-index: 1;
}

.total-count-card {
  max-width: 100%;
  box-sizing: border-box;
}

.total-count-card .card {
  background: linear-gradient(135deg, #2C7BE5 0%, #1a5cbd 100%);
  box-shadow: 0 4px 20px rgba(44, 123, 229, 0.3);
  max-width: 100%;
  box-sizing: border-box;
}
</style>
